import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Clock,
  Trophy,
  Wallet,
} from "lucide-react";
import axios from "../../../../axios";
import { errorToast } from "../../../../utils/toast";
import { useUser } from "../../../../hooks/use-user";
import Loding from "../../../../componant/Loader/Loding";
import { FaAngleLeft } from "react-icons/fa";
import coinIcon from "../../../../Images/L2E_single_coin.png";
import winnerBadge from "../../../../Images/winnerbadge.png";
import rejectedBadge from "../../../../Images/rejectedBadge.png";
import { getContestReturnUrl } from "./contestNavigation";

const ContestResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const returnUrl = location.state?.returnUrl || getContestReturnUrl();
  const dummyResult = location.state?.dummyResult;
  const isDummyContest = Boolean(location.state?.isDummyContest && dummyResult);
  const contestIdFromState = isDummyContest
    ? dummyResult?.contestId
    : location.state?.contestId;
  const { data: user } = useUser();

  const [countData, setCountData] = useState(null);
  const [reportsData, setReportsData] = useState([]);
  const [loading, setLoading] = useState(!isDummyContest);
  const [totalQuestions, setTotalQuestions] = useState(
    dummyResult?.totalQuestions || 0,
  );
  const [hasContestEnded, setHasContestEnded] = useState(isDummyContest);

  useEffect(() => {
    const fetchData = async () => {
      if (isDummyContest) {
        setCountData({
          rightCount: dummyResult?.rightCount || 0,
          wrongCount: dummyResult?.wrongCount || 0,
          totalAttempts:
            (dummyResult?.rightCount || 0) + (dummyResult?.wrongCount || 0),
          accuracy: dummyResult?.accuracy || 0,
          totalTimeTaken: dummyResult?.totalTimeTaken || 0,
          name: dummyResult?.userName,
          contestId: dummyResult?.contestId,
          contestName: "Dummy Contest",
          totalContestSlot: 1,
          userRank: dummyResult?.userRank || 1,
          winningAmount: dummyResult?.winningAmount || 0,
          totalUserContestJoinedCount: dummyResult?.leaderboard?.length || 1,
        });
        setTotalQuestions(dummyResult?.totalQuestions || 0);
        setReportsData(dummyResult?.leaderboard || []);
        setHasContestEnded(true);
        setLoading(false);
        return;
      }

      if (!contestIdFromState) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [detailsRes, questionsRes, reportRes] = await Promise.all([
          axios.get("contestDetails", {
            params: { contestId: contestIdFromState },
          }),
          axios.get(`getContestQuestionsByContestId/${contestIdFromState}`),
          axios.get("getContestReport", {
            params: { contestId: contestIdFromState },
          }),
        ]);

        const details = detailsRes?.data?.result;
        if (details) {
          setHasContestEnded(details.hasContestEnded || false);
          setCountData({
            rightCount: details.totalRightAnswerCount || 0,
            wrongCount: details.totalWrongAnswerCount || 0,
            totalAttempts: details.totalAttempts || 0,
            accuracy: details.accuracy || 0,
            totalTimeTaken: details.totalTimeTaken || 0,
            name: details.name,
            contestId: details.contestId,
            contestName: details.contestName,
            totalContestSlot: details.totalContestSlot,
            userRank: details.hasContestEnded ? details.userRank : null,
            winningAmount: details.hasContestEnded ? details.winningAmount : 0,
            totalUserContestJoinedCount: details.totalUserContestJoinedCount,
          });
        }

        const questions = questionsRes?.data?.result?.questions;
        if (Array.isArray(questions)) {
          setTotalQuestions(questions.length);
        }

        const report = reportRes?.data?.result;
        if (Array.isArray(report)) {
          setReportsData(report);
        }
      } catch (err) {
        errorToast(err?.response?.data?.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    } else if (isDummyContest) {
      fetchData();
    }
  }, [user, contestIdFromState, isDummyContest, dummyResult]);

  const rightCount = countData?.rightCount || 0;
  const wrongCount = countData?.wrongCount || 0;
  const totalAttempts = countData?.totalAttempts || rightCount + wrongCount;
  const skippedCount = Math.max(totalQuestions - totalAttempts, 0);
  const accuracy = countData?.accuracy || 0;
  const winningAmount = countData?.winningAmount || 0;
  const winningAmountValue = Number(winningAmount || 0);
  const userRank = countData?.userRank;
  const score = useMemo(
    () => Math.max(rightCount - wrongCount * 0.25, 0),
    [rightCount, wrongCount],
  );
  const isRejected = hasContestEnded && winningAmountValue <= 0;

  const walletBalance =
    (user?.unifiedWallet?.rechargedBalance || 0) +
    (user?.unifiedWallet?.contestBalance || 0) +
    (user?.unifiedWallet?.referralBalance || 0) +
    (user?.unifiedWallet?.lockedBalance || 0);

  const formatScore = (value) => {
    const rounded = Number(value || 0).toFixed(2);
    return rounded.endsWith(".00")
      ? String(Math.round(Number(rounded)))
      : rounded.replace(/0$/, "");
  };

  const formatTime = (seconds) => {
    if (!seconds) return "0s";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getPlayerName = (entry) =>
    entry?.user?.name || entry?.name || "Rajesh Patil";

  const getPlayerScore = (entry) =>
    entry?.score ?? entry?.contestScore ?? entry?.rightCount ?? 0;

  const getPlayerPrize = (entry) =>
    entry?.prizeWon || entry?.winningAmount || entry?.coins || 0;

  const displayName = countData?.name || user?.name || "User";
  const displayContestId = countData?.contestId || contestIdFromState;
  const isTopRank = Number(userRank || 0) >= 1 && Number(userRank || 0) <= 3;
  const isRankOne = Number(userRank || 0) === 1;
 

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#EFE7F5]">
        <Loding />
      </div>
    );
  }

  if (!contestIdFromState && !isDummyContest) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#EFE7F5] px-4">
        <div className="text-center">
          <p className="mb-4 text-lg text-[#562566]">No contest data found</p>
          <button
            onClick={() => navigate(returnUrl)}
            className="rounded-xl bg-[#562566] px-6 py-2 font-bold text-white"
          >
            Go to Contests
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#D9D6DD]">
      <style>
        {`
          @keyframes contest-result-spin {
            from { transform: rotate(0deg) scale(1); }
            to { transform: rotate(360deg) scale(1); }
          }

          @keyframes contest-result-pulse {
            0%, 100% { opacity: 0.55; }
            50% { opacity: 0.9; }
          }
        `}
      </style>

      <div className="mx-auto min-h-screen max-w-[430px] pb-8 lg:max-w-[760px] lg:pt-8">
        <div
          className={`relative overflow-hidden px-4 pb-6 pt-4 lg:rounded-t-[28px] ${
            isTopRank && !isRejected
              ? "bg-gradient-to-br from-[#FFF6DE] via-[#F6D487] to-[#C78126]"
              : "bg-gradient-to-br from-[#F7F2FB] to-[#E9E0F2]"
          }`}
        >
          {isRankOne && !isRejected && (
            <div className="pointer-events-none absolute left-1/2 top-[150px] h-[430px] w-[430px] -translate-x-1/2 -translate-y-1/2 overflow-visible rounded-full">
              <div
                className="relative h-full w-full"
                style={{
                  animation:
                    "contest-result-spin 3.2s linear infinite, contest-result-pulse 1.4s ease-in-out infinite",
                }}
              >
                {Array.from({ length: 12 }).map((_, index) => (
                  <span
                    key={index}
                    className="absolute left-1/2 top-1/2 h-9 w-[220px] origin-left rounded-full bg-white/70 blur-[1px]"
                    style={{ transform: `rotate(${index * 30}deg)` }}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="relative z-10 flex items-center justify-between lg:pt-8">
            <button
              type="button"
              onClick={() => navigate(returnUrl)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/75 text-[#2C2432] shadow-sm"
            >
              <FaAngleLeft size={18} />
            </button>

            <div className="flex items-center gap-2 rounded-full bg-[#FFF4D8] px-3 py-2 text-xs font-bold text-[#562566] shadow-sm">
              <Wallet size={15} />
              {formatScore(walletBalance)}
            </div>
          </div>

          <div className="relative z-10 mt-4 flex justify-center">
            {isRejected ? (
              <div className="relative flex flex-col items-center">
                <img
                  src={rejectedBadge}
                  alt="Rejected"
                  className="h-28 w-28 object-contain drop-shadow-xl"
                />
                <div className="-mt-3 rounded-xl bg-white px-6 py-2 text-xs font-black uppercase text-[#562566] shadow">
                  Reject
                </div>
              </div>
            ) : (
              <div className="relative flex flex-col items-center">
                <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-[#9B59D0] to-[#4A235A] shadow-xl">
                  <img
                    src={winnerBadge}
                    alt="Winner badge"
                    className="h-20 w-20 object-contain"
                  />
                </div>
                <div className="-mt-3 rounded-xl bg-gradient-to-r from-[#FFBD12] to-[#F59E0B] px-7 py-2 text-xs font-black uppercase text-[#7A3F00] shadow-lg">
                  Rank {userRank || "--"}
                </div>
              </div>
            )}
          </div>

          <div className="relative z-10 mt-6 flex items-center gap-3">
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                displayName,
              )}&background=CD3D3D&color=fff&size=128`}
              alt="avatar"
              className="h-20 w-20 rounded-full border-[3px] border-[#EFD4A6] object-cover"
            />

            <div className="min-w-0 flex-1">
              <h2
                className={`truncate text-[22px] font-extrabold ${
                  isTopRank && !isRejected ? "text-[#2D1A10]" : "text-[#562566]"
                }`}
              >
                {displayName}
              </h2>
              <p
                className={`truncate text-[11px] font-medium ${
                  isTopRank && !isRejected ? "text-[#5E4520]" : "text-[#4B3C63]"
                }`}
              >
                Contest ID : {displayContestId}
              </p>

              <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#562566] to-[#6D38A3] px-4 py-2 text-sm font-bold text-white shadow">
                <Trophy size={17} className="text-[#FFB31A]" />
                {isRejected
                  ? "No Rewards"
                  : `${winningAmountValue} Rewards`}
              </div>
            </div>
          </div>

          <div className="relative z-10 mt-5 grid grid-cols-2 gap-2">
            {[
              ["Total Questions", totalQuestions],
              ["Correct Answer", rightCount],
              ["Incorrect Answer", wrongCount],
              ["Attempted Questions", totalAttempts],
            ].map(([label, value], index) => (
              <div
                key={label}
                className="min-h-[82px] rounded-xl border border-[#E7C680] bg-[#FFFAF1]/95 px-3 py-3 text-center"
              >
                <p className="min-h-[30px] text-[11px] font-semibold leading-4 text-[#684921]">
                  {label}
                </p>
                <p
                  className={`mt-1 text-[22px] font-black ${
                    index === 1
                      ? "text-[#10A65A]"
                      : index === 2
                        ? "text-[#E5305D]"
                        : "text-[#562566]"
                  }`}
                >
                  {formatScore(value).padStart(2, "0")}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="px-4">
          <div className="-mt-4 flex overflow-hidden rounded-2xl bg-[#562566] text-center text-white shadow-lg">
            <div className="flex-1 py-3">
              <p className="text-3xl font-black">
                {formatScore(score).padStart(2, "0")}
              </p>
              <p className="mt-1 text-[13px] font-bold text-[#D9C8F0]">
                {isRejected ? "Avg Score" : "Score"}
              </p>
            </div>
            <div className="my-3 w-px bg-white/25" />
            <div className="flex-1 py-3">
              <p className="text-3xl font-black">
                {formatScore(accuracy)}
                {!isRejected && "%"}
              </p>
              <p className="mt-1 text-[13px] font-bold text-[#D9C8F0]">
                Accuracy
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs text-[#562566]">
            <div className="rounded-xl bg-white p-3 shadow-sm">
              <Clock className="mx-auto mb-1" size={16} />
              {formatTime(countData?.totalTimeTaken)}
            </div>
            <div className="rounded-xl bg-white p-3 shadow-sm">
              <Trophy className="mx-auto mb-1" size={16} />
              Rank {userRank || "-"}
            </div>
            <div className="rounded-xl bg-white p-3 shadow-sm">
              <span className="block font-black">{skippedCount}</span>
              Skipped
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl bg-[#D1CDD8]">
            <div className="bg-gradient-to-r from-[#F5A623] to-[#E8940E] py-3 text-center text-xl font-bold text-white">
              Top Ranks
            </div>

            <div className="space-y-2 p-3">
              {(reportsData.length ? reportsData : []).slice(0, 10).map(
                (entry, index) => {
                  const rank = index + 1;
                  const isCurrentUser =
                    entry?.user?._id === user?._id || entry?._id === user?._id;
                  const isPodium = rank <= 3;

                  return (
                    <div
                      key={entry?._id || index}
                      className={`flex items-center justify-between gap-3 rounded-2xl px-3 py-3 ${
                        isPodium || isCurrentUser
                          ? "bg-[#562566] text-white"
                          : "bg-[#F1EEF5] text-[#1F182F]"
                      }`}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <div
                          className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${
                            isPodium ? "bg-[#7E6A9A]" : "bg-[#CCC4D9]"
                          }`}
                        >
                          <img
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                              getPlayerName(entry),
                            )}&background=7E6A9A&color=fff&size=96`}
                            alt=""
                            className="h-full w-full rounded-full object-cover"
                          />
                          <span className="absolute -bottom-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#FFBD12] px-1 text-[9px] font-black text-[#6D3C00]">
                            {rank}
                          </span>
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-extrabold">
                            {isCurrentUser ? "You" : getPlayerName(entry)}
                          </p>
                          <p
                            className={`text-xs ${
                              isPodium || isCurrentUser
                                ? "text-[#E2D2F7]"
                                : "text-[#675A7B]"
                            }`}
                          >
                            Contest Score : {getPlayerScore(entry)}
                          </p>
                        </div>
                      </div>

                      <div
                        className={`flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs font-black ${
                          isPodium || isCurrentUser
                            ? "bg-[#6D38A3] text-white"
                            : "bg-[#562566] text-white"
                        }`}
                      >
                        <img
                          src={coinIcon}
                          alt=""
                          className="h-4 w-4 object-contain"
                        />
                        {getPlayerPrize(entry)}
                      </div>
                    </div>
                  );
                },
              )}

              {!reportsData.length && (
                <div className="rounded-xl bg-white px-4 py-8 text-center text-sm text-gray-500">
                  Rankings are being calculated. Please check again shortly.
                </div>
              )}
            </div>
          </div>

          {!hasContestEnded && (
            <p className="mt-4 rounded-lg bg-white px-4 py-3 text-center text-sm font-semibold text-[#562566]">
              Final rank and rewards will be available after the contest ends.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContestResultPage;
